import React, { useState, useRef, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Typography
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Send as SendIcon } from "@material-ui/icons";

import AssetUploaderManager from "../AssetUploader/UploadStep/AssetUploaderManager";
import AssetNaming from "../AssetUploader/NamingStep/AssetNaming";
import AssetOptions from "../AssetUploader/OptionsStep/AssetOptions";
import AssetReview from "../AssetUploader/ReviewStep/AssetReview";
import SmartTrafficReview from "../AssetUploader/ReviewStep/SmartTrafficReview";
import MatchPreviewStep from "../AssetUploader/MatchPreviewStep/MatchPreviewStep";
import BackupStep from "../AssetUploader/BackupStep/BackupStep";
import CompanionStep from "../AssetUploader/CompanionStep/CompanionStep";
import Confirmation from "../Shared/Confirmation/Confirmation";

import { useSelector, useDispatch, useStore } from "react-redux";
import {
  clearSelectedCreatives,
  setCreativeApiError,
  setRotationType,
  setWeightDistribution,
  setWeightDistributionFlagOnly,
  setStartDate,
  setEndDate,
  setHardCutoff,
  setDynamicTargetingKeys
} from "../../actions/app/creatives";
import { ROTATION_TYPES, WEIGHT_OPTIONS } from "../../actions/app/constants";
import {
  useUserAdvertisers,
  ADVERTISER_STATUS
} from "../../hooks/useUserAdvertisers";
import { insertCreatives } from "../../services/dcm/creatives";
import {
  matchCreativesToPlacements,
  applyOverrides,
  groupAssignmentsForSubmission,
  getCreativeType,
  buildPlacementDurations,
  buildSiteServedIds,
  getAdGroupLabel,
  CREATIVE_TYPE
} from "../../utils/creativeMatching";
import { getDurationOptions } from "../AssetUploader/Shared/dimensionUtils";
import {
  createAdSettingsState,
  captureAdSettings,
  defaultAdSettings,
  resolveAdSettings,
  recordAdSettings,
  setAdCustomized,
  isAdCustomized,
  countSharedAds
} from "../../utils/adSettings";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  stepper: {
    margin: "20px auto 30px",
    width: "60%",
    paddingTop: 0
  }
}));

const next = {
  float: "right"
};

// Rich media is existing-only: it is authored in Studio, so it never arrives
// through the upload zone, but it has to be findable in "Choose from
// existing". Leaving these out meant CM360 was asked for display, video and
// audio and dutifully returned no rich media at all — the whole reason Smart
// Traffic could not traffic it. Same three types DisplayStepper and
// RichMediaStepper already request, which covers every active rich media
// creative in the account.
const creativeTypes = [
  "DISPLAY",
  "RICH_MEDIA_DISPLAY_BANNER",
  "RICH_MEDIA_DISPLAY_EXPANDING",
  "RICH_MEDIA_DISPLAY_INTERSTITIAL",
  "INSTREAM_VIDEO",
  "INSTREAM_AUDIO",
  // Site-served placements take a tracking creative and nothing else. These
  // are existing-only — a tracking creative has no asset, so there is
  // nothing to upload — and the picker only fetches them when a site-served
  // placement is actually selected.
  "TRACKING_TEXT"
];

function shouldUploadBackup(creatives) {
  for (const c of creatives) {
    if (c.creativeRole === "BACKUP") continue;
    if (c.file && c.file.type && c.file.type.includes("zip")) return true;
  }
  return false;
}

// A primary's BACKUP/COMPANION rides along in the same ad and needs to share
// its weight-recompute scope (see calculateEvenWeights's numberOfBackups),
// so every caller that scopes creativeIds to a group derives them here.
function getLinkedCreatives(primaryIds, byId) {
  const ids = new Set(primaryIds);
  for (const pid of primaryIds) {
    const primary = byId[pid];
    if (!primary) continue;
    if (primary.companionId) ids.add(primary.companionId);
    if (primary.backupId) ids.add(primary.backupId);
  }
  return Array.from(ids)
    .map((id) => byId[id])
    .filter(Boolean);
}

// Steps before "Match preview" are conditional (H5 backup, companion), so the
// ad-options steps' starting index has to be computed the same way here and
// in getStepContent/the nav effect, or they'd disagree on which step index
// belongs to which ad.
function getAdOptionsStartIndex({ isH5, wantsCompanion }) {
  let index = 1; // Upload creative(s)
  if (isH5) index += 1;
  if (wantsCompanion) index += 1;
  index += 1; // Generate name(s)
  index += 1; // Match preview
  return index;
}

function getSteps(stepOptions) {
  const steps = ["Upload creative(s)"];
  if (stepOptions.isH5) steps.push("Upload backup creative(s)");
  if (stepOptions.wantsCompanion) steps.push("Add companion creative(s)");
  // Existing GCM creatives already carry their name — this step becomes a
  // "Save URL" pass over them, so label it honestly (matches Audio/Display/
  // VideoStepper).
  steps.push(
    stepOptions.allExisting ? "Review URL(s)" : "Generate name(s)"
  );
  steps.push("Match preview");
  const groups = stepOptions.matchGroups || [];
  if (groups.length === 0) {
    steps.push("Select Options");
  } else {
    groups.forEach((group, i) => steps.push(`Ad ${i + 1} Options`));
  }
  steps.push("Review");
  return steps;
}

function getStepContent(stepIndex, props) {
  const steps = [];

  steps.push(
    <div>
      <AssetUploaderManager
        isThumbnailEnabled={true}
        filetype={"image/*, .zip, video/*, audio/*"}
        creativeTypes={creativeTypes}
        selectedCreatives={props.selectedCreatives}
        setSelectedCreatives={props.setSelectedCreatives}
        placements={props.placements}
        canChooseExisting={true}
        ref={(r) => (props.stepRef.current = r)}
        headerExtra={
          props.hasMedia ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.wantsCompanion}
                  onChange={(e) => props.setWantsCompanion(e.target.checked)}
                  color="primary"
                />
              }
              label="Add companion creatives for media files (image/HTML5 displayed alongside the media player)"
              style={{ marginBottom: 8 }}
            />
          ) : null
        }
      />
    </div>
  );

  if (props.isH5) {
    steps.push(
      <div>
        <BackupStep
          isClickEnabled={true}
          isDragDisabled={true}
          isThumbnailEnabled={false}
          ref={(r) => (props.stepRef.current = r)}
        />
      </div>
    );
  }

  if (props.wantsCompanion) {
    steps.push(
      <div>
        <CompanionStep
          isClickEnabled={true}
          isDragDisabled={true}
          selectedCreatives={props.selectedCreatives}
          setSelectedCreatives={props.setSelectedCreatives}
          isThumbnailEnabled={false}
          ref={(r) => (props.stepRef.current = r)}
        />
      </div>
    );
  }

  steps.push(
    <div>
      {/* Smart Traffic is the one flow that takes a whole mixed batch in a
          single upload, so the naming list clusters cards by dimension and
          each group can be selected in one click — the taxonomy form already
          saves across the whole selection. */}
      <AssetNaming
        isClickEnabled={true}
        isDragDisabled={true}
        isThumbnailEnabled={true}
        groupByDimension={true}
        ref={(r) => (props.stepRef.current = r)}
      />
    </div>
  );

  steps.push(
    <div>
      <MatchPreviewStep
        matchOverrides={props.matchOverrides}
        setMatchOverrides={props.setMatchOverrides}
        adOverrides={props.adOverrides}
        setAdOverrides={props.setAdOverrides}
        matchGroups={props.matchGroups}
        ref={(r) => (props.stepRef.current = r)}
      />
    </div>
  );

  const groups = props.matchGroups || [];

  if (groups.length === 0) {
    steps.push(
      <div>
        <AssetOptions
          isClickEnabled={false}
          isDragDisabled={false}
          isThumbnailEnabled={true}
          ref={(r) => (props.stepRef.current = r)}
        />
      </div>
    );
  } else {
    groups.forEach((group, i) => {
      const customized = props.isAdCustomized(group.key);
      const sharedCount = props.sharedAdCount;

      steps.push(
        <div key={group.key}>
          <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
            {`Ad ${i + 1}: ${getAdGroupLabel(group)} (${group.creativeIds.length} creative${
              group.creativeIds.length === 1 ? "" : "s"
            })`}
          </Typography>
          {/* Settings are the same across a campaign far more often than
              not, so one shared set is the default and standing out is the
              opt-in. Only shown with more than one ad — with a single ad
              there is nothing to share with. */}
          {groups.length > 1 && (
            <div style={{ marginBottom: 8 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={!customized}
                    onChange={(e) =>
                      props.onToggleAdCustomized(group.key, !e.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    Use the same settings as the other ads
                  </Typography>
                }
              />
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
                style={{ marginLeft: 32, marginTop: -4 }}
              >
                {customized
                  ? "Only this ad uses the settings below."
                  : sharedCount > 1
                  ? `Editing these changes all ${sharedCount} ads using shared settings.`
                  : "Uncheck to give this ad its own rotation, dates and targeting keys."}
              </Typography>
            </div>
          )}
          {/* isDragDisabled false so Sequential rotation can be arranged
              here: HorizontalAssetList indexes the filtered list, so a drag
              reorders only this ad's creatives, and that order is what
              groupAssignmentsForSubmission turns into the CM360 sequence. */}
          <AssetOptions
            isClickEnabled={false}
            isDragDisabled={false}
            isThumbnailEnabled={true}
            filterFn={(c) => group.creativeIds.includes(c.id)}
            creativeIds={props.getLinkedCreativeIds(group.creativeIds)}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    });
  }

  // Grouped by ad rather than the flat list the other steppers review, since
  // this is the flow that submits many ads at once — a flat list of 15
  // creatives says nothing about which of 60 placements they land on. Falls
  // back to the flat review when nothing matched, where there are no ads to
  // group by.
  steps.push(
    <div>
      {groups.length === 0 ? (
        <AssetReview
          isClickEnabled={false}
          isDragDisabled={true}
          isThumbnailEnabled={true}
          hasCampaignData={false}
          ref={(r) => (props.stepRef.current = r)}
        />
      ) : (
        <SmartTrafficReview
          adGroups={groups}
          getAdSettings={props.getAdSettingsForGroup}
          isAdCustomized={props.isAdCustomized}
          ref={(r) => (props.stepRef.current = r)}
        />
      )}
    </div>
  );

  return steps[stepIndex] || " ";
}

// insertCreatives() schedules a setTimeout(..., 1000) in its finally block
// that resets smartLoader.loading to false. Wait long enough for that to
// fire before starting the next group, otherwise group 2's loader state
// is clobbered mid-progress.
const LOADER_RESET_GAP_MS = 1100;

export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const store = useStore();
  const [activeStep, setActiveStep] = useState(0);
  const [completed] = useState(new Set());
  const [skipped] = useState(new Set());
  const [selectedCreatives, setSelectedCreatives] = useState([]);
  const [snackbarSeverity, setSnackbarSeverity] = useState("warning");
  // The list itself isn't needed on this route — only whether the account has
  // advertiser access at all, which gates the Next button below.
  const {
    status: advertiserStatus,
    message: advertiserMessage,
    retry: retryUserAdvertisers
  } = useUserAdvertisers();
  const [matchOverrides, setMatchOverrides] = useState({
    added: {},
    removed: {}
  });
  // Manual ad split: placement id -> slot number within its natural ad group.
  // Two placements of the same type, size and spot length share an ad by
  // default; this is how the user says they want them trafficked separately.
  const [adOverrides, setAdOverrides] = useState({});
  const [wantsCompanion, setWantsCompanion] = useState(false);
  // Gates the Confirmation screen. Deriving "done" from
  // activeStep === steps.length is unsafe here because steps.length shrinks
  // with matchGroups: deleting a creative on the Review step (its cards carry
  // a remove button on this route) could drop an ad, leaving activeStep equal
  // to the new steps.length and reporting a successful submission that never
  // happened.
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const creativesState = useSelector((state) => state.creatives);
  const placementInfoData = useSelector((state) => state.placementInfo);

  // Iterate in allIds order, not byId insertion order — allIds is what the
  // drag-and-drop asset list renders and what REORDER_CREATIVES rewrites, so
  // matching (and therefore each ad's submitted creative sequence) follows
  // the order the user actually arranged.
  const allCreativeArray = useMemo(
    () => creativesState.allIds.map((id) => creativesState.byId[id]).filter(Boolean),
    [creativesState.allIds, creativesState.byId]
  );
  const isH5 = shouldUploadBackup(allCreativeArray);
  // Companion opt-in shows only when a primary can actually take one, which
  // means a freshly uploaded audio/video file. In CM360 the companion link is
  // a field on the media creative itself (companionCreatives), and BartAPI
  // only ever sets it while *inserting* that creative
  // (insertMediaCreativeWithCompanion) — there is no update path that attaches
  // a companion to a creative already in GCM. CompanionCreative's
  // isUploadDisabled() enforces the same rule via its dcmId check, so
  // including existing creatives here would just add a companion step whose
  // button is permanently disabled.
  const hasMedia = allCreativeArray.some((c) => {
    if ((c.creativeRole || "PRIMARY") !== "PRIMARY") return false;
    if (c.dcmId || c.dcmCreative) return false;
    const ctype = getCreativeType(c);
    return (
      ctype === CREATIVE_TYPE.INSTREAM_AUDIO ||
      ctype === CREATIVE_TYPE.INSTREAM_VIDEO
    );
  });
  const allExisting =
    allCreativeArray.length > 0 && allCreativeArray.every((c) => c.dcmCreative);

  // Shared helper so the ids are normalized to strings — Placement_Id comes
  // from SQL as a bigint while CM360 ids are strings, and a Set does not
  // coerce.
  const siteServedIds = useMemo(
    () => buildSiteServedIds(placementInfoData.byId),
    [placementInfoData.byId]
  );

  // Spot length per placement, so a :15 doesn't land on a :30 buy. CM360 has
  // no duration for a placement, but the taxonomy-parsed Media_Length on the
  // placementInfo row does — and it is already in this slice.
  const placementDurations = useMemo(
    () => buildPlacementDurations(placementInfoData.byId),
    [placementInfoData.byId]
  );

  const typeGroups = useSelector((state) => state.typeGroups);
  const durationOptions = useMemo(() => getDurationOptions(typeGroups), [
    typeGroups
  ]);

  const placements = creativesState.assignedPlacements || [];

  // Same matching used by MatchPreviewStep and handleTraffic — recomputed
  // here too (cheap, pure) so the ad-options step count/labels always match
  // what Match Preview showed and what will actually be submitted.
  const matchGroups = useMemo(() => {
    const auto = matchCreativesToPlacements(allCreativeArray, placements, {
      siteServedIds,
      placementDurations,
      durationOptions
    });
    const finalAssignments = applyOverrides(auto.matches, matchOverrides);
    return groupAssignmentsForSubmission(
      finalAssignments,
      placements,
      creativesState.allIds,
      { placementDurations, adOverrides, siteServedIds }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allCreativeArray,
    placements,
    siteServedIds,
    placementDurations,
    durationOptions,
    matchOverrides,
    adOverrides,
    creativesState.allIds
  ]);

  const steps = getSteps({ isH5, wantsCompanion, matchGroups, allExisting });

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const stepRef = useRef();

  // Ad settings are shared across every ad by default — in practice they are
  // the same for a whole campaign — with a per-ad opt-out. State drives the
  // checkbox; the mirror ref is what the effect below reads, since that
  // effect deliberately runs only on ad identity and would otherwise close
  // over a stale copy.
  const [adSettings, setAdSettings] = useState(() =>
    createAdSettingsState(null)
  );
  const adSettingsStateRef = useRef(adSettings);
  adSettingsStateRef.current = adSettings;
  const prevAdGroupKeyRef = useRef(null);

  const adOptionsStartIndex = getAdOptionsStartIndex({ isH5, wantsCompanion });
  const adOptionsCount = matchGroups.length > 0 ? matchGroups.length : 1;
  const activeAdGroup =
    matchGroups.length > 0 &&
    activeStep >= adOptionsStartIndex &&
    activeStep < adOptionsStartIndex + adOptionsCount
      ? matchGroups[activeStep - adOptionsStartIndex]
      : null;
  const activeAdGroupKey = activeAdGroup ? activeAdGroup.key : null;

  // Swap the shared rotation/weight/date/DTK fields in and out of the
  // creatives slice as the active ad changes, so each ad's
  // AssetRotation/AssetDelivery/DynamicTargetingKeys (which read/write that
  // single global slice) show and edit only that ad's values.
  useEffect(() => {
    const incomingGroup = activeAdGroup;
    const incomingKey = incomingGroup ? incomingGroup.key : null;
    const outgoingKey = prevAdGroupKeyRef.current;
    let next = adSettingsStateRef.current;

    if (outgoingKey && outgoingKey !== incomingKey) {
      // Writes to the baseline unless that ad opted out, which is how an edit
      // on one shared ad reaches all the others.
      next = recordAdSettings(
        next,
        outgoingKey,
        captureAdSettings(store.getState().creatives)
      );
    }

    if (incomingKey && incomingKey !== outgoingKey) {
      const cs = store.getState().creatives;
      if (!next.shared) {
        next = {
          ...next,
          shared: defaultAdSettings(cs, {
            rotationType: ROTATION_TYPES.WEIGHTED,
            weightDistribution: WEIGHT_OPTIONS.EVEN
          })
        };
      }

      const settings = resolveAdSettings(next, incomingKey);
      const linkedIds = getLinkedCreatives(
        incomingGroup.creativeIds,
        cs.byId
      ).map((c) => c.id);

      dispatch(setRotationType(settings.rotationType));
      // Only the even/custom choice is inherited. EVEN has to be recomputed
      // against this ad's creatives; CUSTOM leaves the per-creative values
      // already in the slice alone, since weights from another ad would be
      // keyed to creatives this ad does not contain.
      if (settings.weightDistribution === WEIGHT_OPTIONS.CUSTOM) {
        dispatch(setWeightDistributionFlagOnly(WEIGHT_OPTIONS.CUSTOM));
      } else {
        dispatch(setWeightDistribution(WEIGHT_OPTIONS.EVEN, linkedIds));
      }
      dispatch(setStartDate(settings.startDate));
      dispatch(setEndDate(settings.endDate));
      dispatch(setHardCutoff(settings.hardCutoff));
      dispatch(setDynamicTargetingKeys(settings.dynamicTargetingKeys));
    }

    if (next !== adSettingsStateRef.current) setAdSettings(next);

    prevAdGroupKeyRef.current = incomingKey;
    // Keyed on the ad's identity, NOT activeStep: a step index does not
    // durably identify an ad, because matchGroups can change under the user
    // (removing a creative on this very step drops or reshuffles groups). On
    // activeStep alone, index N silently becoming a different ad would leave
    // that ad showing the previous ad's settings and then write its edits
    // back onto the previous ad's key. activeAdGroup is intentionally read
    // without being a dep — activeAdGroupKey is its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAdGroupKey]);

  // A failed or empty advertiser check used to be a console.log and a
  // 3-second snackbar, leaving the Next button greyed out with nothing on
  // screen. Keep it visible and recoverable without a re-login.
  const advertiserCheckAlert =
    advertiserStatus === ADVERTISER_STATUS.ERROR ||
    advertiserStatus === ADVERTISER_STATUS.EMPTY ? (
      <MuiAlert
        severity={
          advertiserStatus === ADVERTISER_STATUS.ERROR ? "error" : "warning"
        }
        variant="outlined"
        style={{ marginBottom: 16 }}
        action={
          <Button color="inherit" size="small" onClick={retryUserAdvertisers}>
            Retry
          </Button>
        }
      >
        {advertiserMessage}
      </MuiAlert>
    ) : null;

  const handleNext = () => {
    if (
      stepRef &&
      stepRef.current &&
      stepRef.current.hasOwnProperty("validate")
    ) {
      const validation = stepRef.current.validate();

      if (validation.hasErrors) {
        if (validation.message) {
          setSnackbarMessage(validation.message);
          setSnackbarOpen(true);
        }

        return;
      }
    }

    dispatch(clearSelectedCreatives());
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setHasSubmitted(false);
    setActiveStep(0);
  };

  // matchGroups (and therefore steps.length) can shrink while the user sits
  // on a later step — the asset cards carry a remove button on this route, so
  // deleting the last creative of an ad drops that ad's options step. Pull
  // activeStep back into range so we never render the blank out-of-range
  // step. Skipped once submitted, when activeStep intentionally sits at
  // steps.length so the header shows every step complete.
  useEffect(() => {
    if (hasSubmitted) return;
    if (activeStep > steps.length - 1) {
      setActiveStep(Math.max(0, steps.length - 1));
    }
  }, [activeStep, steps.length, hasSubmitted]);

  const handleStep = (step) => () => {
    // The Stepper header stays mounted on the Confirmation screen, so a
    // StepButton click has to drop out of it — otherwise activeStep moves
    // but Confirmation keeps rendering and the user is stuck there.
    setHasSubmitted(false);
    setSelectedCreatives([]);
    setActiveStep(step);
    dispatch(clearSelectedCreatives());
  };

  const isStepSkipped = (step) => skipped.has(step);
  const isStepComplete = (step) => completed.has(step);

  // Resolves the settings an ad's options step ended on. Falls back to sane
  // defaults for a group whose step was never visited (nonlinear StepButton
  // navigation straight to Review) — weightDistribution defaults to EVEN so
  // handleTraffic's scoped recompute below still runs for it.
  const getAdSettingsForGroup = (group) => {
    // Belt and braces. Submit lives on the Review step, and stepping off the
    // last ad already fires the capture effect (activeAdGroupKey goes null),
    // so in the normal flow nothing is pending here. This only matters if an
    // ad step is somehow still active at submit time, where the slice would
    // hold edits the state has not seen.
    const state =
      prevAdGroupKeyRef.current === group.key
        ? recordAdSettings(
            adSettingsStateRef.current,
            group.key,
            captureAdSettings(store.getState().creatives)
          )
        : adSettingsStateRef.current;

    return (
      resolveAdSettings(state, group.key) ||
      defaultAdSettings(store.getState().creatives, {
        rotationType: ROTATION_TYPES.WEIGHTED,
        weightDistribution: WEIGHT_OPTIONS.EVEN
      })
    );
  };

  // Opting an ad out of the baseline seeds it from what is on screen, so the
  // checkbox never changes a value — it only stops later baseline edits from
  // reaching this ad.
  const handleToggleAdCustomized = (groupKey, customized) => {
    setAdSettings((prev) =>
      setAdCustomized(
        prev,
        groupKey,
        customized,
        captureAdSettings(store.getState().creatives)
      )
    );
  };

  const handleTraffic = async () => {
    // Reuse the same memo the Match Preview and per-ad options steps were
    // built from, rather than recomputing — guarantees what gets submitted
    // (including each ad's creative sequence) is exactly what was shown.
    const groups = matchGroups;

    const failures = [];

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];

      const creativesForGroup = getLinkedCreatives(
        group.creativeIds,
        creativesState.byId
      );
      const linkedIds = creativesForGroup.map((c) => c.id);
      const settings = getAdSettingsForGroup(group);

      // Seed this group's ad-level settings into the shared creatives slice
      // right before its insertCreatives() call — makeAdInfo() reads
      // store.getState().creatives fresh at call time, so each group's ad
      // gets its own rotation/weight/dates instead of whatever the last
      // group left behind.
      dispatch(setRotationType(settings.rotationType));
      if (settings.weightDistribution === WEIGHT_OPTIONS.CUSTOM) {
        // Weights were already set per-creative while this ad's options step
        // was open (or retain their prior value if never visited) — only
        // sync the flag, don't re-zero them.
        dispatch(setWeightDistributionFlagOnly(WEIGHT_OPTIONS.CUSTOM));
      } else {
        dispatch(setWeightDistribution(WEIGHT_OPTIONS.EVEN, linkedIds));
      }
      dispatch(setStartDate(settings.startDate));
      dispatch(setEndDate(settings.endDate));
      dispatch(setHardCutoff(settings.hardCutoff));
      dispatch(setDynamicTargetingKeys(settings.dynamicTargetingKeys));

      // Clear apiError before this group so we can detect *its* outcome.
      // insertCreatives swallows its own errors and dispatches
      // setCreativeApiError(e, true) — we read state after the await to
      // capture the result.
      dispatch(setCreativeApiError(new Error(), false));

      await insertCreatives(creativesForGroup, group.placements, group.type);

      const apiErrorAfter = store.getState().creatives.apiError;
      if (apiErrorAfter && apiErrorAfter.isError) {
        failures.push({ key: group.key, error: apiErrorAfter.error });
      }

      // Wait for insertCreatives's finally-setTimeout to fire (resets the
      // smartLoader). Skip on the last iteration.
      if (i < groups.length - 1) {
        await new Promise((r) => setTimeout(r, LOADER_RESET_GAP_MS));
      }
    }

    if (failures.length) {
      const message = failures
        .map(
          (f) =>
            `${f.key}: ${
              f.error && f.error.message ? f.error.message : f.error
            }`
        )
        .join("\n");
      dispatch(setCreativeApiError(new Error(message), true));
    } else {
      dispatch(setCreativeApiError(new Error(), false));
    }

    // Set before advancing activeStep: these are separate renders (updates
    // after an await aren't batched in React 16), and the clamp effect must
    // already see hasSubmitted before activeStep reaches steps.length.
    setHasSubmitted(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <div className={classes.root}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        nonLinear
        className={classes.stepper}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepButton
                onClick={handleStep(index)}
                completed={isStepComplete(index)}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <Container maxWidth="lg">
        <div>
          {hasSubmitted ? (
            <Confirmation
              handleResetParent={handleReset}
              isMultiAdvertiser={true}
            />
          ) : (
            <div>
              <div style={next}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                  disableElevation
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTraffic}
                    startIcon={<SendIcon />}
                    disableElevation
                  >
                    Submit Creatives
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disableElevation
                    disabled={
                      advertiserStatus === ADVERTISER_STATUS.LOADING ||
                      advertiserStatus === ADVERTISER_STATUS.EMPTY
                    }
                  >
                    Next
                  </Button>
                )}
              </div>
              {advertiserCheckAlert}
              {getStepContent(activeStep, {
                stepRef,
                selectedCreatives,
                setSelectedCreatives,
                placements: creativesState.assignedPlacements,
                matchOverrides,
                setMatchOverrides,
                adOverrides,
                setAdOverrides,
                matchGroups,
                isAdCustomized: (key) => isAdCustomized(adSettings, key),
                onToggleAdCustomized: handleToggleAdCustomized,
                getAdSettingsForGroup,
                sharedAdCount: countSharedAds(
                  adSettings,
                  matchGroups.map((g) => g.key)
                ),
                getLinkedCreativeIds: (primaryIds) =>
                  getLinkedCreatives(primaryIds, creativesState.byId).map(
                    (c) => c.id
                  ),
                isH5,
                hasMedia,
                wantsCompanion,
                setWantsCompanion,
                setActiveStep
              })}
            </div>
          )}
        </div>
      </Container>
      <Snackbar
        open={snackbarOpen}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarSeverity("warning");
        }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
